import {
  doesUserMeetRequirement,
  getBaseFormTemplate,
  getLatestVersion,
  isGuestUserSession,
  type ExtendedThalamosUser,
  type FormContextMiniForm,
} from "@thalamos/aspire-common";

/**
 * The 1-based number of the next part of `form` when only a doctor may sign it
 * and a doctor accreditation is all `user` lacks to do so, otherwise
 * `undefined`.
 *
 * Must agree with the draft gate on which part is next and on exempting guests,
 * or the dialog will contradict whether a draft is refused.
 */
export function partNeedingDoctorAccreditation({
  user,
  form,
}: {
  user: ExtendedThalamosUser;
  form: Pick<
    FormContextMiniForm,
    "formTemplate" | "latestVersion" | "versions"
  >;
}): number | undefined {
  if (isGuestUserSession(user)) return undefined;

  const signedPartsData: unknown = getLatestVersion(form)?.data;
  const partIndex = Array.isArray(signedPartsData) ? signedPartsData.length : 0;
  const requirement =
    getBaseFormTemplate(form)?.parts[partIndex]?.signing.userOrTeamRequirement;

  const onlyDoctorsMaySign =
    !!requirement?.accreditation?.length &&
    requirement.accreditation.every((type) => type === "doctor");
  if (!requirement || !onlyDoctorsMaySign) return undefined;

  const memberships = user.sessionContext ? [user.sessionContext] : [];
  const meetsRequirement = doesUserMeetRequirement(
    { ...user, memberships },
    requirement,
  );
  const wouldMeetRequirementAsDoctor = doesUserMeetRequirement(
    {
      accreditations: [
        ...user.accreditations,
        { type: "doctor", gmcNumber: "unrecorded" },
      ],
      memberships,
    },
    requirement,
  );

  return !meetsRequirement && wouldMeetRequirementAsDoctor
    ? partIndex + 1
    : undefined;
}
